<script setup lang="ts">
import { computed, toRefs } from "vue";
import { useExplorerStore } from "@/modules/explorer/store";
import { useBaseStore } from "@/stores/base";
import SingleMetricType = App.Data.Explorer.SingleMetric.SingleMetricType;
import SingleMetricPickDTO = App.Data.Explorer.SingleMetric.SingleMetricPickDTO;
import {
	mapDimensionNameToIndex,
	pivotBlockReason,
	pivotRowKey,
} from "@/modules/explorer/components/Metrics/MetricHelper";
import DeleteColumnPayload = App.Data.Explorer.SingleMetric.DeleteColumnPayload;

const isContextMenuVisible = defineModel<boolean>();

const props = withDefaults(
	defineProps<{
		position?: { x: number; y: number };
		isColumnSelect: boolean;
		singleMetricConfiguration: SingleMetricPickDTO;
		clickedColumn: any;
		metricSubcolumnCount: number;
		visibleColumnCount: number;
	}>(),
	{
		position: () => ({ x: 0, y: 0 }),
	}
);

const emit = defineEmits<{
	(e: "useSingleMetric"): void;
	(e: "useColumnMetric", type: SingleMetricType): void;
	(e: "setColumnAlert"): void;
	(e: "deleteColumn", type: DeleteColumnPayload): void;
	(e: "hideColumn", columnKey: string): void;
	(e: "pivotColumn", columnKey: string): void;
	(e: "unpivotColumn"): void;
}>();

const { position, isColumnSelect } = toRefs(props);
const explorerStore = useExplorerStore();

const setSingleMetric = () => {
	emit("useSingleMetric");
};

const setColumnMetric = (type) => {
	emit("useColumnMetric", type);
};

const setColumnAlert = () => {
	emit("setColumnAlert");
};

const columnType = computed(() => {
	return isDimension.value ? "dimension" : "metric";
});

const isDimension = computed(() => {
	const header = props.singleMetricConfiguration.header;
	if (!header) {
		return false;
	}
	return header.column.depth === 0 && !headerTreeHasMeta(header);
});

const isHighestLevelColumn = computed(() => {
	const header = props.singleMetricConfiguration.header;
	if (!header) {
		return false;
	}
	return (
		header.column.depth === 0 &&
		headerTreeHasMeta(header) &&
		!header.column.columnDef.meta?.metricName
	);
});

function getColumnNameClean() {
	if (isDimension.value) {
		const accessorKey =
			props.singleMetricConfiguration.header.column.columnDef.accessorKey;
		const dimensionIndex = mapDimensionNameToIndex(accessorKey);
		return explorerStore.document.data_tables[
			props.singleMetricConfiguration.table_index
		].dimensions[dimensionIndex].name_clean;
	} else {
		return getLeafColumnNameClean(props.singleMetricConfiguration.header);
	}
}

function getLeafColumnNameClean(header) {
	if (Array.isArray(header)) {
		for (const h of header) {
			const result = getLeafColumnNameClean(h);
			if (result) return result;
		}
		return null;
	}

	const metricName = header.column.columnDef.meta?.metricName;
	if (metricName) {
		return metricName;
	}
	if (header.subHeaders?.length > 0) {
		return getLeafColumnNameClean(header.subHeaders);
	}

	return null;
}

const deleteColumn = () => {
	emit("deleteColumn", {
		type: columnType.value,
		nameClean: getColumnNameClean(),
		key: props.singleMetricConfiguration.header.id,
	});
};
const hideColumn = () => {
	emit("hideColumn", props.singleMetricConfiguration.header.id);
};

const pivotColumn = () => {
	emit("pivotColumn", props.singleMetricConfiguration.header.id);
};

const unpivotColumn = () => {
	emit("unpivotColumn");
};

function headerTreeHasMeta(header) {
	if (header.column.columnDef.meta?.metricName) return true;

	if (header.subHeaders?.length) {
		return header.subHeaders.some((child) => headerTreeHasMeta(child));
	}

	return false;
}

const deleteActionShouldBeDisabled = () => {
	if (isDimension.value) {
		return (
			explorerStore.document.data_tables[
				props.singleMetricConfiguration.table_index
			].dimensions.length < 2
		);
	}
	return (
		explorerStore.document.data_tables[
			props.singleMetricConfiguration.table_index
		].metrics.length < 2 || props.visibleColumnCount < 2
	);
};

const hideColumnActionShouldBeDisabled = () => {
	return props.metricSubcolumnCount < 2;
};

const isPivoted = computed(() => {
	return (
		pivotRowKey(
			explorerStore.document.data_tables[
				props.singleMetricConfiguration.table_index
			]
		) === props.singleMetricConfiguration.header?.column.id
	);
});

const pivotBlock = computed(() =>
	pivotBlockReason(
		explorerStore.document.data_tables[
			props.singleMetricConfiguration.table_index
		],
		explorerStore.document.date_comparison
	)
);

const actions = computed(() => {
	if (isColumnSelect.value) {
		return [
			{
				title: "Sum",
				icon: "fa-regular fa-plus",
				action: () => setColumnMetric("sum"),
				disabled: false,
				visible: !isDimension.value && !isHighestLevelColumn.value,
				disabledTooltipText: "",
			},
			{
				title: "Average",
				icon: "fa-regular fa-divide",
				action: () => setColumnMetric("avg"),
				disabled: false,
				visible: !isDimension.value && !isHighestLevelColumn.value,
				disabledTooltipText: "",
			},
			{
				title: "Minimum",
				icon: "fa-regular fa-sort-numeric-down",
				action: () => setColumnMetric("min"),
				disabled: false,
				visible: !isDimension.value && !isHighestLevelColumn.value,
				disabledTooltipText: "",
			},
			{
				title: "Maximum",
				icon: "fa-regular fa-sort-numeric-up",
				action: () => setColumnMetric("max"),
				disabled: false,
				visible: !isDimension.value && !isHighestLevelColumn.value,
				disabledTooltipText: "",
			},
			{
				title: "Define Alert",
				icon: "fa-regular fa-bell-on",
				action: () => setColumnAlert(),
				disabled: false,
				visible:
					useBaseStore().currentUser?.derivedPermissions.includes(
						"can_manage_alerts"
					) &&
					!isDimension.value &&
					!isHighestLevelColumn.value,
				disabledTooltipText: "",
			},
			{
				title: "Delete column",
				icon: "fa-regular fa-trash",
				action: () => deleteColumn(),
				disabled: deleteActionShouldBeDisabled(),
				visible:
					props.clickedColumn.depth === 1 ||
					props.singleMetricConfiguration.header.column.depth === 0,
				disabledTooltipText: `At least one ${columnType.value} must remain.`,
			},
			{
				title: "Hide column",
				icon: "fa-regular fa-eye-slash",
				action: () => hideColumn(),
				disabled: hideColumnActionShouldBeDisabled(),
				visible:
					!isDimension.value &&
					!isHighestLevelColumn.value &&
					props.clickedColumn.depth !== 1,
				disabledTooltipText: "At least one metric must remain visible.",
			},
			{
				title: "Pivot column",
				icon: "fa-solid fa-table-pivot",
				action: () => pivotColumn(),
				disabled: pivotBlock.value !== null,
				visible:
					(props.clickedColumn.depth === 1 ||
						props.singleMetricConfiguration.header.column.depth === 0) &&
					isDimension.value &&
					!isPivoted.value,
				disabledTooltipText: pivotBlock.value ?? "",
			},
			{
				title: "Unpivot column",
				icon: "fa-solid fa-table-pivot",
				action: () => unpivotColumn(),
				disabled: false,
				visible:
					(props.clickedColumn.depth === 1 ||
						props.singleMetricConfiguration.header.column.depth === 0) &&
					isDimension.value &&
					isPivoted.value,
				disabledTooltipText: "",
			},
		];
	}

	return [
		{
			title: "Use as single metric",
			icon: "fa-regular fa-gauge-high",
			action: setSingleMetric,
			visible: true,
			disabled: false,
		},
	];
});
</script>

<template>
	<v-menu
		v-model="isContextMenuVisible"
		:close-on-content-click="true"
		:nudge-width="200"
		location-strategy="connected"
		:target="[position.x, position.y]"
		absolute
	>
		<v-list density="compact">
			<div v-for="action in actions" :key="action.title">
				<v-list-item
					v-if="action.visible"
					class="font-weight-regular text-caption"
					:disabled="action.disabled"
					@click="action.action"
				>
					<template v-slot:prepend>
						<i class="mr-3" :class="action.icon"></i>
					</template>
					{{ action.title }}
				</v-list-item>
				<v-tooltip
					v-if="action.disabled"
					activator="parent"
					location="bottom"
					>{{ action.disabledTooltipText }}</v-tooltip
				>
			</div>
		</v-list>
	</v-menu>
</template>

<style scoped></style>
